import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
  OnInit,
  signal,
  ViewChild,
} from "@angular/core";import { OverlayContainer } from "@angular/cdk/overlay";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatMenuModule, MatMenuTrigger } from "@angular/material/menu";
import { MatSelectModule } from "@angular/material/select";
import { Card } from "../../common/components/general/card/card";
import { MatBadgeModule } from "@angular/material/badge";
import { MenuService } from "./menu-service";
import { CommonModule } from "@angular/common";
import { Icon } from "../../common/components/general/icon/icon";
import { AppLinks, ChildMenu, ParentMenu } from "../header-model";
import { Router, RouterModule } from "@angular/router";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MenuOverlayEventsService } from "./menu-overlay-events.service";
import { ReusableService } from "../../common/services/reusable-services";
import { HeaderService } from "../header.service";
import { Label } from "../../common/models/page-components";
import { COMPONENT_META_DATA } from "../../common/constants/component-meta-data";
import { ContentFilterPipe } from "../../common/pipes/content-filter-pipe";
import { FormsModule } from "@angular/forms";
import { SrpSnackbarService } from '../../common/services/srp-snackbar.service';
import { AnalyticsService } from "../../common/services/analytics-service";

@Component({
  selector: "srp-menu",
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    MatMenuModule,
    Card,
    MatBadgeModule,
    MatMenuTrigger,
    RouterModule,
    Icon,
    ContentFilterPipe,
    FormsModule
  ],
  providers: [ContentFilterPipe],
  templateUrl: "./menu.html",
  styleUrl: "./menu.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: "srp-app-wrapper" },
})
export class Menu implements OnInit {
  @Input() pageContentLabels: Label[] = [];
  @Input() appLinks: AppLinks;
  @Input() icon = "";
  @Input() appContext: string;

  @ViewChild(MatMenuTrigger) matMenuTrigger!: MatMenuTrigger;

  readonly menuService = inject(MenuService);
  readonly router = inject(Router);
  readonly snackBar = inject(MatSnackBar);
  readonly menuEventService = inject(MenuOverlayEventsService);
  readonly overlayContainer = inject(OverlayContainer);
  readonly reusableService = inject(ReusableService);
  readonly elRef = inject(ElementRef);
  readonly headerService = inject(HeaderService);
  readonly srpSnackbarService = inject(SrpSnackbarService);
  readonly analyticsService = inject(AnalyticsService);

  // favLabels: Label[];
  // pageState = signal({
  //   favLabels: []
  // });
  labels: Label[];
  favMsg: boolean;
  LABELS = COMPONENT_META_DATA.HEADER.LABELS;
  favorites = signal<ChildMenu[]>(null);
  parentMenu = signal<ParentMenu[]>(null);
  isMenuOpen = false;
  expandedSubmenus = signal<string[]>([]);
  loaded = signal(false);
  contentPipe = inject(ContentFilterPipe);

  isSubmenuOpen(child: ChildMenu) {
    const key = child?.name || child?.label || '';
    return (this.expandedSubmenus() || []).includes(key);
  }

  toggleSubmenu(child: ChildMenu, event?: MouseEvent) {
    if (event) event.stopPropagation();
    const key = child?.name || child?.label || '';
    const current = this.expandedSubmenus() || [];
    if (current.includes(key)) {
      this.expandedSubmenus.set(current.filter(k => k !== key));
    } else {
      this.expandedSubmenus.set([...current, key]);
    }
  }

  toggleMenu() {
    this.isMenuOpen = true;
    this.matMenuTrigger.openMenu();
    const { updatedParentMenu, updatedFavorites } = this.menuService.refreshCatalogInApplicability(this.parentMenu(), this.favorites());
    this.parentMenu.set(updatedParentMenu);
    this.favorites.set(updatedFavorites);
  }

  menuClosed(): void {
    this.isMenuOpen = false;
    this.matMenuTrigger.closeMenu();
  }

  ngOnInit(): void {
    
    this.overlayContainer
      .getContainerElement()
      .classList.add("srp-app-wrapper");

    this.headerService.getHeaderResponseFromObservable().subscribe(async response => {
      if (response?.data?.appLinks) {
        const { parentMenuLinks, favoriteCMs } = await this.menuService.fetchMenuWithFavorites(response.data.appLinks);
        this.parentMenu.set(parentMenuLinks);
        this.favorites.set(favoriteCMs);
        this.loaded.set(!!favoriteCMs?.length);
                this.labels = response.data.labels;

      } else {
        this.loaded.set(false);
      }

    });
  }
  addFavoriteItem(event: MouseEvent, link: ChildMenu, parentLabel: string) {
    event.stopPropagation();
    const visibleCount = (this.favorites() ?? []).filter(f => f.visible !== false).length;
    if (visibleCount >= 10) {
      this.showSnackbar();
      return;
    }

    this.updateFavoriteMenuItems(link, parentLabel, true);
  }

  removeFavoriteItem(event: MouseEvent, link: ChildMenu, parentLabel: string) {
    event.stopPropagation();
    this.updateFavoriteMenuItems(link, parentLabel, false);
  }

  private updateFavoriteMenuItems(link: ChildMenu, parentLabel: string, shouldFavorite: boolean) {
    let requestSent = false;

    const updatedParents = (this.parentMenu() ?? []).map((parent) => {
      if (parentLabel && parent.label !== parentLabel) return parent;

    const updatedChildren = parent.childMenu.map((child) => {
        // Direct child match (second-level)
        if (this.isSameMenuItem(child, link)) {
          const updatedChild = { ...child, isFavorite: shouldFavorite };
            if (!requestSent) {
              this.sendFavoriteAddOrRemove(link, parentLabel, updatedChild, shouldFavorite);
              requestSent = true;
          }
          return updatedChild;
        }

        // Check nested childMenu (third-level)
       if (child.childMenu?.length) {
          const updatedSubmenu = child.childMenu.map((sub) => {
            if (!this.isSameMenuItem(sub, link)) return sub;
            const updatedSub = { ...sub, isFavorite: shouldFavorite };
              if (!requestSent) {
                this.sendFavoriteAddOrRemove(link, parentLabel, updatedSub, shouldFavorite);
                requestSent = true;
            }
            return updatedSub;
          });
          return { ...child, childMenu: updatedSubmenu };
        }
        return child;
      });

      return { ...parent, childMenu: updatedChildren };
    });

    this.parentMenu.set(updatedParents);

    // Fallback for favourites not present in the parentMenu tree (e.g. AEM-only favourites).
    // Only applies when no specific parent was targeted (parentLabel falsy), so an
    // explicit but non-matching parentLabel still results in no request being sent.
    if (!requestSent && !parentLabel) {
        this.sendFavoriteAddOrRemove(link, parentLabel, { ...link, isFavorite: shouldFavorite }, shouldFavorite);
    }
  }

  private isSameMenuItem(menu: ChildMenu, link: ChildMenu): boolean {
    if (link.name && menu.name) return menu.name === link.name;
    return !!link.label && !!menu.label && menu.label === link.label;
  }

  private sendFavoriteAddOrRemove(link: ChildMenu, parentLabel: string, updatedChild: ChildMenu, shouldFavorite: boolean) {
    if (shouldFavorite) {
      this.sendAddFavoriteRequest(link, parentLabel, updatedChild);
      return;
    }
    this.sendRemoveFavoriteRequest(link, parentLabel, updatedChild);
  }

  private sendAddFavoriteRequest(link: ChildMenu, parentLabel: string, updatedChild: ChildMenu) {
    this.addFavorite(link, parentLabel, updatedChild);
  }

  private sendRemoveFavoriteRequest(link: ChildMenu, parentLabel: string, updatedChild: ChildMenu) {
    this.removeFavorite(link, parentLabel, updatedChild);
  }

  private showSnackbar() {
    this.srpSnackbarService.show({
      message: this.contentPipe.transform(this.labels,this.LABELS.FAVS_LIMIT_MSG),
      type: 'alert',
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top'
    });
  }

  private addFavorite(link: ChildMenu, parentLabel: string, updatedChild: ChildMenu) {
    this.menuService.addOrRemoveFavorite(link, parentLabel).subscribe();
    this.favorites.update((favs) => {
      const current = favs ?? [];
      const exists = current.some(fav => fav.name === link.name);
      if (exists) return current;
      const toAdd = { ...updatedChild, visible: updatedChild.visible ?? true } as ChildMenu;
      return [...current, toAdd];
    });
    // Optimistically notify other consumers (e.g. Easy Access on the landing page)
    // instantly instead of waiting for the add API call to resolve.
    this.menuService.favsChanged.next({ ...updatedChild, parentMenuName: parentLabel, isFavorite: true });
  }

  private removeFavorite(link: ChildMenu, parentLabel: string, updatedChild: ChildMenu) {
    this.menuService.addOrRemoveFavorite(link, parentLabel, true).subscribe();
    this.favorites.update((favs) =>
      (favs ?? []).filter((fav) => fav.name !== link.name)
    );
    // Optimistically notify other consumers so the item disappears from
    // Easy Access / landing page immediately when the remove icon is clicked.
    this.menuService.favsChanged.next({ ...updatedChild, parentMenuName: parentLabel, isFavorite: false });
  }

  onLinkClick(menu: ChildMenu) {
    if (menu.disabled) {
      return;
    }
    const event = new CustomEvent('menuLinkClick', {
      detail: menu,
      bubbles: true,
      composed: true,
    });
    this.elRef.nativeElement.dispatchEvent(event);
    this.menuEventService.menuClick(menu);

    this.analyticsService.trackClickEvent(
      'uaevent',
      'Expandbar',
      'Redirection::External',
      menu.label
    );

    this.reusableService.navigateToUrl(menu, this.appContext, menu.label);

    // ✅ Close hamburger menu after click
    this.isMenuOpen = false;
    this.matMenuTrigger.closeMenu();
  }

  onSublevelClick(subItem: any, parentLabel?: string) {
    if (!subItem) return;
    const mapped: ChildMenu = new ChildMenu({
      label: subItem.sublevelLabel || subItem.label || subItem.sublevelName || '',
      link: subItem.sublevelLink || subItem.link || '',
      linkType: subItem.sublevelLinkType || subItem.linkType || 'default',
      category: subItem.sublevelCategory || subItem.category || 'external',
      authField: subItem.sublevelAuthField || subItem.authField || '',
      catalogId: subItem.sublevelCatalogId || subItem.catalogId || '',
      name: subItem.sublevelName || subItem.name || '',
      visible: true,
    });

    this.onLinkClick(mapped);
  }
  hasThirdLevel(child: any): boolean {
  return !!(
    child?.subLabel &&
    child?.sublabelchildMenu &&
    child.sublabelchildMenu.length > 0
  );
}

isThirdLevelOpen(child: any): boolean {
  const key = `sub-${child?.name || child?.label}`;
  return this.expandedSubmenus().includes(key);
}

toggleThirdLevel(child: any, event?: MouseEvent): void {
  if (event) {
    event.stopPropagation();
  }

  const key = `sub-${child?.name || child?.label}`;
  const current = this.expandedSubmenus();

  if (current.includes(key)) {
    this.expandedSubmenus.set(
      current.filter(item => item !== key)
    );
  } else {
    this.expandedSubmenus.set([
      ...current,
      key
    ]);
  }
}
}
